import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { apiError, blobApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { money, num, pct, dateShort, marginColor, marginBg } from "@/lib/format";
import { QUOTE_STATUSES, STATUS_COLORS, CARRIER_LOGO_COLOR } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import CustomerSummary from "@/components/workspace/CustomerSummary";
import DiagnosticsDrawer from "@/components/workspace/DiagnosticsDrawer";
import EmailQuoteDialog from "@/components/workspace/EmailQuoteDialog";
import BolDialog from "@/components/workspace/BolDialog";
import BolViewerDialog from "@/components/workspace/BolViewerDialog";
import ResyncBolDialog from "@/components/workspace/ResyncBolDialog";
import BookingConfirmation from "@/components/workspace/BookingConfirmation";
import BookingEmailDialog from "@/components/workspace/BookingEmailDialog";
import ContextDiscussion from "@/components/chat/ContextDiscussion";
import RouteMap from "@/components/workspace/RouteMap";
import AerialViewDialog from "@/components/AerialViewDialog";
import StackBreakdown from "@/components/workspace/StackBreakdown";
import GtzBookDialog from "@/components/GtzBookDialog";
import FedExBookDialog from "@/components/FedExBookDialog";
import ShipmentStatusCard from "@/components/ShipmentStatusCard";
import DeliveryDocs from "@/components/workspace/DeliveryDocs";
import { ArrowLeft, Copy, Pencil, Stethoscope, FileText, Loader2, Clock, User, Zap, Download, AlertTriangle, Mail, ClipboardList, Send, Video, Building2, Truck, CheckCircle2, MapPin } from "lucide-react";

function Tile({ label, value, sub, accent }) {
  return (
    <div className="border border-border bg-card p-4">
      <div className="label-caps">{label}</div>
      <div className={`metric-num mt-2 text-xl font-bold ${accent || ""}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

// Detect an in-progress "@query" at the caret for the note mention picker.
function activeMention(value, caret) {
  const upto = value.slice(0, caret);
  const m = upto.match(/(^|\s)@(\S*)$/);
  if (!m) return null;
  return { at: caret - m[2].length - 1, query: m[2] };
}

// Turn an audit entry's structured `changes` into short human-readable bits. Service additions/
// removals live in the entry's `reason` summary, so they're intentionally skipped here.
function auditBits(a) {
  const c = a.changes || {};
  const bits = [];
  if (c.carrier) bits.push(`Carrier: ${String(c.carrier).replace(/_/g, " ")}`);
  if (c.class) bits.push(`Class: ${c.class.from ?? "—"} → ${c.class.to ?? "—"}`);
  if (c.weight) bits.push(`Weight: ${c.weight.from ?? "—"} → ${c.weight.to ?? "—"} lb`);
  if (c.status) bits.push(`Status: ${c.status.from ?? "—"} → ${c.status.to ?? "—"}`);
  if (c.override_price != null) bits.push(`Override: $${c.override_price}`);
  else if (c.sell_price != null) bits.push(`Sell: $${c.sell_price}`);
  if (c.items != null) bits.push(`${c.items} item(s)`);
  // FedEx booking attempts (success + failure) — surface the identifiers for support/audit.
  if (c.account) bits.push(`Acct ${c.account}${c.payment_type ? " · " + c.payment_type : ""}`);
  if (c.pro) bits.push(`PRO ${c.pro}`);
  if (c.transaction_id) bits.push(`Txn ${c.transaction_id}`);
  if (c.shipper) bits.push(`From ${c.shipper}`);
  return bits;
}

function RateTerminals({ quoteId, rate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadOnce = (open) => {
    if (!open || data || loading) return;
    setLoading(true);
    api.get(`/quotes/${quoteId}/terminals`, { params: { carrier: rate.carrier_code } })
      .then((r) => setData(r.data))
      .catch(() => setData({ note: "Unable to load terminals right now." }))
      .finally(() => setLoading(false));
  };

  const renderTerminal = (t, fallbackCode) => {
    if (!t && fallbackCode) t = { code: fallbackCode };
    if (!t) return <div className="text-sm text-muted-foreground">Not available</div>;
    const title = t.name ? `${t.name}${t.code ? ` (${t.code})` : ""}` : (t.code ? `Terminal ${t.code}` : "—");
    return (
      <div className="text-sm">
        <div className="font-semibold text-foreground">{title}</div>
        {t.street && <div className="text-muted-foreground">{t.street}</div>}
        {(t.city || t.state || t.zip) && <div className="text-muted-foreground">{[t.city, t.state, t.zip].filter(Boolean).join(", ")}</div>}
        {t.phone && <div className="text-muted-foreground">P: {t.phone}</div>}
        {t.distance_mi != null && <div className="text-muted-foreground">≈ {t.distance_mi} mi to stop</div>}
      </div>
    );
  };

  return (
    <Popover onOpenChange={loadOnce}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-[hsl(var(--primary))]"
          data-testid={`rate-terminals-${rate.carrier_code}`}
        >
          <MapPin className="h-3 w-3" /> Terminals
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 font-heading text-sm font-bold">LTL Terminals</div>
        {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
        <div className="space-y-2">
          <div>
            <div className="label-caps text-muted-foreground">Pickup Terminal</div>
            {renderTerminal(data?.origin, rate.gtz_origin_terminal_code)}
          </div>
          <div className="border-t border-border pt-2">
            <div className="label-caps text-muted-foreground">Delivery Terminal</div>
            {renderTerminal(data?.destination, rate.gtz_dest_terminal_code)}
          </div>
        </div>
        {data?.note && <div className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">{data.note}</div>}
      </PopoverContent>
    </Popover>
  );
}

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromShipments = location.state?.from === "/shipments";
  const backTo = cameFromShipments ? "/shipments" : "/history";
  const backLabel = cameFromShipments ? "Back to Shipments" : "Back to Quote History";
  const { isAdmin, user: me } = useAuth();
  const [q, setQ] = useState(null);
  const [audit, setAudit] = useState([]);
  const [showCustomer, setShowCustomer] = useState(false);
  const [showDiag, setShowDiag] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showBol, setShowBol] = useState(false);
  const [showBolViewer, setShowBolViewer] = useState(false);
  const [bolBlobUrl, setBolBlobUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState("");
  const [rlPushing, setRlPushing] = useState(false);
  const [showAerial, setShowAerial] = useState(false);
  const [showGtzBook, setShowGtzBook] = useState(false);
  const [showFedexBook, setShowFedexBook] = useState(false);
  const [showBookingEmail, setShowBookingEmail] = useState(false);
  const [showResync, setShowResync] = useState(false);
  const [selectingCarrier, setSelectingCarrier] = useState("");
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [noteMentions, setNoteMentions] = useState([]);
  const [mentionState, setMentionState] = useState(null);
  const noteRef = useRef(null);
  const { users, refreshUnread } = useChat();
  const mentionMatches = useMemo(() => {
    if (!mentionState) return [];
    const qy = mentionState.query.toLowerCase();
    return users.filter((u) => u.id !== me?.id && (u.name || "").toLowerCase().includes(qy)).slice(0, 6);
  }, [mentionState, users, me]);

  const [terminals, setTerminals] = useState(null);

  const load = () => {
    api.get(`/quotes/${id}`).then((r) => setQ(r.data)).catch((e) => { toast.error(apiError(e)); navigate("/history"); });
    api.get(`/quotes/${id}/audit`).then((r) => setAudit(r.data)).catch(() => {});
    api.get(`/quotes/${id}/terminals`).then((r) => setTerminals(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const onNoteChange = (e) => {
    setNoteText(e.target.value);
    setMentionState(activeMention(e.target.value, e.target.selectionStart || e.target.value.length));
  };
  const pickMention = (u) => {
    if (!mentionState) return;
    const before = noteText.slice(0, mentionState.at);
    const after = noteText.slice(noteRef.current?.selectionStart || noteText.length);
    setNoteText(`${before}@${u.name} ${after}`);
    setNoteMentions((m) => (m.find((x) => x.id === u.id) ? m : [...m, { id: u.id, name: u.name }]));
    setMentionState(null);
    setTimeout(() => noteRef.current?.focus(), 0);
  };
  const onNoteKeyDown = (e) => {
    if (mentionState && mentionMatches.length && (e.key === "Enter" || e.key === "Tab")) {
      e.preventDefault(); pickMention(mentionMatches[0]); return;
    }
    if (e.key === "Escape") setMentionState(null);
  };
  const activePings = () => noteMentions.filter((m) => noteText.includes(`@${m.name}`));
  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    const ids = activePings().map((m) => m.id);
    setAddingNote(true);
    try {
      const r = await api.post(`/quotes/${id}/note`, { note: text, mentions: ids });
      setNoteText(""); setNoteMentions([]); setMentionState(null);
      api.get(`/quotes/${id}/audit`).then((res) => setAudit(res.data)).catch(() => {});
      refreshUnread?.();
      toast.success(r.data?.mentioned ? `Note added · ${r.data.mentioned} teammate(s) pinged` : "Note added to the audit log");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAddingNote(false);
    }
  };

  const handleBolGenerated = (blobUrl) => { setBolBlobUrl(blobUrl || null); load(); setShowBolViewer(true); };
  const handleResynced = () => { setBolBlobUrl(null); load(); setShowBolViewer(true); };
  const closeBolViewer = (v) => {
    setShowBolViewer(v);
    if (!v && bolBlobUrl) { window.URL.revokeObjectURL(bolBlobUrl); setBolBlobUrl(null); }
  };

  if (!q) return <div className="text-muted-foreground">Loading quote…</div>;

  const s = q.stack_result || {};
  const fin = q.financial || {};
  const rates = q.rates || [];
  const HIDDEN_CARRIERS = ["tax air", "performance freight", "central transport"];
  const sortedRates = [...rates]
    .filter((r) => !HIDDEN_CARRIERS.some((k) => (r.carrier_name || "").toLowerCase().includes(k)))
    .sort(
      (a, b) => (Number(a.total_carrier_cost <= 0) - Number(b.total_carrier_cost <= 0)) || (a.total_carrier_cost - b.total_carrier_cost),
    );
  const bestCarrierCode = q.recommended_carrier_code || sortedRates.find((r) => r.total_carrier_cost > 0)?.carrier_code;
  const selectedRate = rates.find((r) => r.carrier_code === q.selected_carrier_code);
  const isBooked = !!(q.gtz_bol_number || q.bol_pro_number || q.status === "booked");
  const rlDelivered = !!q.delivered_at || (!!q.tracking?.final && (q.tracking?.status || "").toLowerCase().includes("deliver"));
  const rlBolModifiable = !!q.bol_pro_number && !rlDelivered;
  const bk = q.booking || {};
  const bookedCost = bk.cost ?? q.gtz_booked_cost ?? q.carrier_cost;
  const bookedCarrier = bk.carrier || q.gtz_carrier_name || (q.bol_pro_number ? "R+L Carriers" : (q.selected_carrier_code || "").replace("_", " "));

  const changeStatus = async (status) => {
    try { await api.put(`/quotes/${id}`, { status }); setQ({ ...q, status }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const selectCarrier = async (code) => {
    if (isBooked || code === q.selected_carrier_code) return;
    setSelectingCarrier(code);
    try {
      await api.put(`/quotes/${id}`, { selected_carrier_code: code });
      const name = sortedRates.find((r) => r.carrier_code === code)?.carrier_name || code;
      toast.success(`Carrier set to ${name} — cost & margin updated`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSelectingCarrier(""); }
  };
  const duplicate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/quotes/${id}/duplicate`); toast.success(`Duplicated as ${data.quote_number}`); navigate(`/quotes/${data.id}`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const downloadPdf = async (copy) => {
    setPdfBusy(copy);
    try {
      const res = await api.get(`/quotes/${id}/pdf?copy=${copy}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${q.quote_number}_${copy}.pdf`; a.rel = "noopener";
      document.body.appendChild(a); a.click();
      toast.success(`Downloaded ${q.quote_number}_${copy}.pdf`);
      setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 4000);
    } catch (e) { toast.error(apiError(e)); } finally { setPdfBusy(""); }
  };

  const previewFedexBol = async () => {
    setPdfBusy("fedexbol");
    try {
      const res = await api.get(`/quotes/${id}/fedex-bol/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(await blobApiError(e)); } finally { setPdfBusy(""); }
  };

  const previewFedexLabel = async () => {
    setPdfBusy("fedexlabel");
    try {
      const res = await api.get(`/quotes/${id}/fedex-label/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(await blobApiError(e)); } finally { setPdfBusy(""); }
  };

  const previewGtzCarrierBol = async () => {
    setPdfBusy("gtzcarrierbol");
    try {
      const res = await api.get(`/quotes/${id}/gtz-carrier-bol/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); } finally { setPdfBusy(""); }
  };

  const pushRlPortal = async () => {
    setRlPushing(true);
    try {
      const { data } = await api.post(`/quotes/${id}/rl-portal-push`);
      if (data.status === "pushed") toast.success(`Pushed to R+L portal — Quote #${data.standard_quote_number}`);
      else toast.error(data.error || "R+L portal push failed");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setRlPushing(false); }
  };

  const custForm = {
    customer_name: q.customer_name, quote_ref: q.quote_ref, dest_city: q.dest_city,
    dest_state: q.dest_state, destination_zip: q.destination_zip, fedex_service_type: q.fedex_service_type,
  };
  const custResult = { stack_result: q.stack_result, calculated_class: q.calculated_class, diagnostics: q.diagnostics, warnings: q.warnings };

  const hasDestination = !!(q.dest_address1 || q.destination_zip || q.dest_city);
  const aerialRoute = {
    id: q.id,
    customer: q.customer_name,
    dest_company: q.dest_company,
    dest_address1: q.dest_address1,
    has_street_address: !!(q.dest_address1 && q.dest_address1.trim()),
    has_bol: !!q.bol_pro_number,
    bol_pro_number: q.bol_pro_number,
    value: q.sell_price,
    status: q.status,
    origin: q.origin_zip ? { city: q.origin_zip, state: "" } : null,
    destination: { city: q.dest_city || q.destination_zip, state: q.dest_state },
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <button onClick={() => navigate(backTo)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-history">
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-black tracking-tighter">{q.quote_number}</h1>
            <button
              onClick={() => { navigator.clipboard.writeText(q.quote_number).then(() => toast.success(`${q.quote_number} copied`)).catch(() => toast.error("Copy failed")); }}
              title="Copy quote number" data-testid="detail-copy-quote-number"
              className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
              <Copy className="h-4 w-4" />
            </button>
            <Select value={q.status} onValueChange={changeStatus}>
              <SelectTrigger className={`h-8 w-32 border text-[11px] font-bold uppercase tracking-wider ${STATUS_COLORS[q.status] || ""}`} data-testid="detail-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{QUOTE_STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>{q.customer_name || "No customer"}</span>
            {q.quote_ref && <span>Ref: {q.quote_ref}</span>}
            <span className="flex items-center gap-1"><User className="h-3 w-3" /> {q.created_by}</span>
            {q.source === "api" && (
              <span className="rounded bg-[hsl(var(--primary))]/15 px-1.5 py-0.5 font-bold uppercase text-[hsl(var(--primary))]" data-testid="detail-api-source">
                via API · {q.source_app || "external app"}
              </span>
            )}
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {dateShort(q.created_at)}</span>
            <span>{q.origin_zip || "—"} → {q.destination_zip || "—"}</span>
            {q.bol_pro_number && (
              <button
                onClick={() => { setBolBlobUrl(null); setShowBolViewer(true); }}
                data-testid="detail-bol-pro"
                className="rounded bg-[hsl(var(--success))]/15 px-1.5 py-0.5 font-semibold text-[hsl(var(--success))] transition-colors hover:bg-[hsl(var(--success))]/25"
                title="View Bill of Lading"
              >
                R+L BOL: {q.bol_pro_number} · View
              </button>
            )}
            {q.gtz_bol_number && (
              <button
                onClick={previewGtzCarrierBol}
                data-testid="detail-gtz-carrier-bol"
                className="rounded bg-[hsl(var(--primary))]/15 px-1.5 py-0.5 font-semibold text-[hsl(var(--primary))] transition-colors hover:bg-[hsl(var(--primary))]/25"
                title="View the Trifecta Transport (GlobalTranz carrier) BOL"
              >
                Trifecta BOL: {q.gtz_bol_number} · View
              </button>
            )}
            {q.fedex_pro_number && (
              <button
                onClick={previewFedexBol}
                data-testid="detail-fedex-bol"
                className="rounded bg-[hsl(var(--success))]/15 px-1.5 py-0.5 font-semibold text-[hsl(var(--success))] transition-colors hover:bg-[hsl(var(--success))]/25"
                title="Booked via FedEx Freight — view the legal Straight Bill of Lading"
              >
                FedEx BOL: {q.fedex_pro_number} · View
              </button>
            )}
            {q.fedex_pro_number && (
              <button
                onClick={previewFedexLabel}
                data-testid="detail-fedex-label"
                className="rounded bg-[hsl(var(--primary))]/15 px-1.5 py-0.5 font-semibold text-[hsl(var(--primary))] transition-colors hover:bg-[hsl(var(--primary))]/25"
                title="View the FedEx Freight shipping label (goes on the pallet)"
              >
                FedEx Label · View
              </button>
            )}
            {q.selected_carrier_code === "RL_CARRIERS" && q.rl_portal_push && (
              q.rl_portal_push.status === "pushed" ? (
                <span data-testid="rl-portal-status" className="rounded bg-blue-500/15 px-1.5 py-0.5 font-semibold text-blue-600" title={`Standard $${q.rl_portal_push.standard_total ?? "—"} · pushed to your R+L MyRLC Activity Queue`}>
                  R+L Portal: #{q.rl_portal_push.standard_quote_number}
                </span>
              ) : q.rl_portal_push.status === "pending" ? (
                <span className="text-amber-600">R+L portal: pushing…</span>
              ) : (
                <span className="text-destructive" title={q.rl_portal_push.error}>R+L portal: push failed</span>
              )
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isBooked && (
            <>
              <Button variant="outline" onClick={() => downloadPdf("customer")} disabled={pdfBusy === "customer"} data-testid="detail-pdf-customer">
                {pdfBusy === "customer" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}Customer PDF
              </Button>
              {isAdmin && (
                <Button variant="outline" onClick={() => downloadPdf("internal")} disabled={pdfBusy === "internal"} data-testid="detail-pdf-internal">
                  {pdfBusy === "internal" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}Internal PDF
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowCustomer(true)} data-testid="detail-customer"><FileText className="mr-1.5 h-4 w-4" />Preview</Button>
            </>
          )}
          <Button variant="outline" onClick={() => setShowEmail(true)} data-testid="detail-email"><Mail className="mr-1.5 h-4 w-4" />Email</Button>
          {rlBolModifiable && (
            <Button variant="outline" onClick={() => setShowResync(true)} data-testid="detail-modify-bol"
              title="Edit this BOL in the R+L portal (same pro), then re-pull the updated official BOL here">
              <Pencil className="mr-1.5 h-4 w-4" />Modify BOL
            </Button>
          )}
          {!isBooked && (
            <>
              <Button variant="outline" onClick={() => setShowBol(true)} data-testid="detail-bol"><ClipboardList className="mr-1.5 h-4 w-4" />{q.bol_pro_number ? "BOL" : "Book R+L / BOL"}</Button>
              {q.selected_carrier_code === "RL_CARRIERS" && (
                <Button variant="outline" onClick={pushRlPortal} disabled={rlPushing} data-testid="detail-rl-portal-push" title="Submit this quote into your R+L MyRLC portal (Activity Queue)">
                  {rlPushing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
                  {q.rl_portal_push?.status === "pushed" ? "Re-push to R+L" : "Push to R+L Portal"}
                </Button>
              )}
              {selectedRate?.broker === "GLOBALTRANZ" && !q.gtz_bol_number && (
                <Button variant="outline" onClick={() => setShowGtzBook(true)} data-testid="detail-gtz-book" title="Book the selected GlobalTranz rate as a real shipment (returns a GTZ BOL number)">
                  <Truck className="mr-1.5 h-4 w-4" />Book via GlobalTranz
                </Button>
              )}
              {selectedRate?.carrier_code === "FEDEX_FREIGHT" && !q.fedex_pro_number && (
                <Button variant="outline" onClick={() => setShowFedexBook(true)} data-testid="detail-fedex-book" title="Book the selected FedEx Freight rate as a real shipment (creates the BOL)">
                  <Truck className="mr-1.5 h-4 w-4" />Book via FedEx
                </Button>
              )}
            </>
          )}
          <Button variant="outline" onClick={() => setShowDiag(true)} data-testid="detail-diagnostics"><Stethoscope className="mr-1.5 h-4 w-4" />Diagnostics</Button>
          <Button
            variant="outline"
            disabled={q.status === "booked" || !!q.bol_pro_number}
            onClick={() => navigate("/workspace", { state: { editQuoteId: q.id } })}
            data-testid="detail-edit"
            title={q.bol_pro_number ? "Booked quotes can't be edited — duplicate instead" : "Edit this quote"}
          >
            <Pencil className="mr-1.5 h-4 w-4" />Edit
          </Button>
          <Button onClick={duplicate} disabled={busy} data-testid="detail-duplicate" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Copy className="mr-1.5 h-4 w-4" />Duplicate</>}
          </Button>
        </div>
      </div>

      {(() => {
        const fallbacks = q.live_errors || [];
        const selEstimate = selectedRate && selectedRate.rate_source !== "live" && selectedRate.rate_source !== "manual";
        if (!selEstimate && fallbacks.length === 0) return null;
        return (
          <div className="rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-4 py-3" data-testid="phs-estimate-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--warning))]" />
              <div className="text-sm">
                {selEstimate && <p className="font-semibold text-[hsl(var(--warning))]">Selected rate is a PHS Estimate, not a live carrier rate — verify before committing this price to a customer.</p>}
                {fallbacks.length > 0 && (
                  <div className={selEstimate ? "mt-1" : ""}>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Live carrier fallbacks</span>
                    <ul className="mt-0.5 space-y-0.5 text-xs text-muted-foreground">
                      {fallbacks.map((f, i) => (
                        <li key={i}><span className="font-semibold">{(f.carrier_code || "").replace("_", " ")}:</span> {f.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      <BookingConfirmation
        q={q}
        onViewBol={() => { setBolBlobUrl(null); setShowBolViewer(true); }}
        onEmail={() => setShowBookingEmail(true)}
      />

      <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-4 xl:grid-cols-8">
        <Tile label="Product" value={q.product_name} sub={`Qty ${num(q.quantity)}`} />
        <Tile label="Cargo Weight" value={`${num(s.total_cargo_weight)} lb`} />
        <Tile label="Footprints" value={num(s.total_stack_footprints)} sub={`${s.full_stack_count} full · ${s.last_stack_pieces > 0 ? 1 : 0} partial`} />
        <Tile label="Tallest Stack" value={`${num(s.tallest_stack_height, 1)}"`} />
        <Tile label="Freight Class" value={q.calculated_class ? `Class ${q.calculated_class}` : "—"} sub={q.fak?.applied ? `FAK ${q.fak.fak_class}` : "NMFC"} accent="text-[hsl(var(--primary))]" />
        {isBooked ? (
          <>
            <Tile label="Booked Cost" value={money(bookedCost)} sub="true cost at booking" accent="text-[hsl(var(--success))]" />
            <Tile label="Carrier" value={bookedCarrier} sub={q.gtz_bol_number ? "via GlobalTranz" : q.fedex_pro_number ? "via FedEx" : "via R+L"} />
            <Tile label={q.gtz_bol_number ? "GTZ BOL #" : q.fedex_pro_number ? "FedEx PRO #" : "R+L PRO #"} value={q.gtz_bol_number || q.fedex_pro_number || q.bol_pro_number || "—"} sub={bk.pickup_scheduled ? "pickup scheduled" : "BOL only"} />
          </>
        ) : (
          <>
            <Tile label="Carrier Cost" value={money(q.carrier_cost)} sub={q.selected_carrier_code?.replace("_", " ")} />
            <Tile label="Sell Price" value={money(q.sell_price)} accent="text-foreground" />
            <Tile label="Margin" value={pct(q.margin_pct)} sub={money(q.margin_dollars)} accent={marginColor(fin.margin_health)} />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {isBooked ? (
            <>
              <ShipmentStatusCard quote={q} onReset={load} />
              <DeliveryDocs quoteId={q.id} gtzBol={q.gtz_bol_number} />
            </>
          ) : (
          <div className="border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="font-heading text-base font-bold">Carrier Rates</span>
              <span className="hidden text-xs font-normal text-muted-foreground sm:inline">Click any carrier to select it — cost &amp; margin update instantly</span>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Carrier</TableHead><TableHead>Service</TableHead>
                    <TableHead className="text-right">Transit</TableHead><TableHead className="text-right">Linehaul</TableHead>
                    <TableHead className="text-right">Fuel</TableHead><TableHead className="text-right">Access.</TableHead>
                    <TableHead className="text-right">Total</TableHead><TableHead>Source</TableHead>
                    <TableHead className="text-center">Select</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRates.map((r) => {
                    const sel = r.carrier_code === q.selected_carrier_code;
                    const isBest = r.carrier_code === bestCarrierCode;
                    const canSelect = r.total_carrier_cost > 0;
                    const selecting = selectingCarrier === r.carrier_code;
                    return (
                      <TableRow
                        key={r.carrier_code}
                        data-testid={`detail-carrier-row-${r.carrier_code}`}
                        onClick={() => canSelect && selectCarrier(r.carrier_code)}
                        className={`border-border ${canSelect ? "cursor-pointer" : ""} ${sel ? "bg-[hsl(var(--primary))]/8" : isBest ? "bg-[hsl(var(--success))]/8 hover:bg-[hsl(var(--success))]/12" : canSelect ? "hover:bg-secondary/50" : ""}`}
                        style={sel ? { boxShadow: "inset 3px 0 0 hsl(var(--primary))" } : isBest ? { boxShadow: "inset 3px 0 0 hsl(var(--success))" } : {}}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: CARRIER_LOGO_COLOR[r.carrier_code] || "#888" }} />
                            <span className="text-sm font-semibold">{r.carrier_name}</span>
                            {isBest && (
                              <span className="flex items-center gap-1 rounded-full bg-[hsl(var(--success))] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white" data-testid="detail-best-price">
                                <Zap className="h-3 w-3" /> Best Price
                              </span>
                            )}
                            {sel && !isBest && <Zap className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />}
                          </div>
                          {(r.gtz_origin_terminal_code || r.gtz_dest_terminal_code || r.carrier_code === "RL_CARRIERS" || r.carrier_code === "FEDEX_FREIGHT") && (
                            <div className="mt-1"><RateTerminals quoteId={id} rate={r} /></div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{r.service_type || "—"}</TableCell>
                        <TableCell className="metric-num text-right text-sm">{r.transit_days ? `${r.transit_days}d` : "—"}</TableCell>
                        <TableCell className="metric-num text-right text-sm">{money(r.linehaul)}</TableCell>
                        <TableCell className="metric-num text-right text-sm text-muted-foreground">{money(r.fuel_surcharge)}</TableCell>
                        <TableCell className="metric-num text-right text-sm text-muted-foreground">{money(r.accessorial_charges)}</TableCell>
                        <TableCell className={`metric-num text-right text-sm font-bold ${isBest ? "text-[hsl(var(--success))]" : ""}`}>{money(r.total_carrier_cost)}</TableCell>
                        <TableCell className={`text-[10px] font-bold uppercase tracking-wider ${r.broker === "PRIORITY1" ? "text-[#F47C20]" : r.broker === "GLOBALTRANZ" ? "text-[#0072CE]" : r.rate_source === "live" ? "text-[hsl(var(--success))]" : r.rate_source === "manual" ? "text-zinc-400" : "text-[hsl(var(--warning))]"}`} data-testid={`detail-rate-source-${r.carrier_code}`}>
                          {r.broker === "PRIORITY1" ? "via Priority1" : r.broker === "GLOBALTRANZ" ? "via GlobalTranz" : r.rate_source === "live" ? "Direct API" : (r.rate_label || r.rate_source)}
                        </TableCell>
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                          {canSelect ? (
                            <button
                              onClick={() => selectCarrier(r.carrier_code)}
                              disabled={selecting || sel}
                              data-testid={`detail-select-carrier-${r.carrier_code}`}
                              title={sel ? "Selected carrier for this quote" : "Select this carrier"}
                              className="inline-flex items-center justify-center disabled:cursor-default"
                            >
                              {selecting ? (
                                <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--primary))]" />
                              ) : (
                                <span className={`flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${sel ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]" : "border-border hover:border-[hsl(var(--primary))]"}`}>
                                  {sel && <CheckCircle2 className="h-4 w-4 text-white" />}
                                </span>
                              )}
                            </button>
                          ) : (
                            <span className="text-xs text-muted-foreground/40">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
          )}

          {(s.line_items?.length || s.full_stack || s.last_stack) && <StackBreakdown stack={s} calculatedClass={q.calculated_class} />}

          {q.notes && (
            <div className="border border-border bg-card p-4">
              <div className="label-caps">Notes</div>
              <p className="mt-2 text-sm">{q.notes}</p>
            </div>
          )}

          <div className="border border-border bg-card" data-testid="audit-log">
            <div className="border-b border-border px-4 py-3 font-heading text-base font-bold">Audit Log</div>
            <div className="border-b border-border px-4 py-3">
              <div className="relative">
                <Textarea
                  ref={noteRef}
                  value={noteText}
                  onChange={onNoteChange}
                  onKeyDown={onNoteKeyDown}
                  rows={2}
                  placeholder="Add a note…  @ to mention a teammate"
                  className="text-sm"
                  data-testid="quote-note-input"
                />
                {mentionState && mentionMatches.length > 0 && (
                  <div className="absolute bottom-full left-2 z-30 mb-1 w-60 overflow-hidden rounded-lg border border-border bg-popover shadow-lg" data-testid="quote-mention-list">
                    {mentionMatches.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => pickMention(u)}
                        data-testid={`quote-mention-${u.id}`}
                        className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-secondary"
                      >
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[hsl(var(--primary))]/15 text-[10px] font-bold text-[hsl(var(--primary))]">{(u.name || "U").slice(0, 2).toUpperCase()}</span>
                        <span className="truncate text-sm">{u.name}</span>
                        <span className="ml-auto text-[10px] uppercase text-muted-foreground">{u.role === "admin" ? "admin" : "rep"}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="truncate text-[11px] text-muted-foreground">
                  {activePings().length > 0 ? `Will ping: ${activePings().map((m) => m.name).join(", ")}` : "Type @ to notify a teammate"}
                </span>
                <Button size="sm" onClick={addNote} disabled={addingNote || !noteText.trim()} data-testid="quote-note-submit">
                  {addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Note"}
                </Button>
              </div>
            </div>
            <div className="divide-y divide-border">
              {audit.length === 0 && <div className="px-4 py-3 text-sm text-muted-foreground">No audit entries.</div>}
              {audit.map((a) => {
                const bits = auditBits(a);
                return (
                  <div key={a.id} className="flex items-start justify-between gap-4 px-4 py-2.5 text-sm" data-testid={`audit-entry-${a.action}`}>
                    <div>
                      <span className="font-semibold capitalize">{a.action === "note" ? "Note" : a.action}</span>
                      {a.reason && <span className="text-muted-foreground"> — {a.reason}</span>}
                      {bits.length > 0 && (
                        <div className="mt-0.5 text-xs text-muted-foreground">{bits.join("  ·  ")}</div>
                      )}
                    </div>
                    <div className="shrink-0 text-right text-xs text-muted-foreground">
                      <div>{a.user_name}</div><div>{dateShort(a.timestamp)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <RouteMap originZip={q.origin_zip} destZip={q.destination_zip} pickupDate={q.pickup_date ? dateShort(q.pickup_date) : ""} />

          {terminals && (
            <div className="border border-border bg-card" data-testid="ltl-terminals">
              <div className="border-b border-border px-4 py-3 font-heading text-base font-bold">LTL Terminals</div>
              <div className="divide-y divide-border">
                {[["Origin · Pickup", terminals.origin], ["Destination · Delivery", terminals.destination]].map(([label, t]) => (
                  <div key={label} className="px-4 py-3" data-testid={`terminal-${label.startsWith("Origin") ? "origin" : "destination"}`}>
                    <div className="label-caps text-muted-foreground">{label}</div>
                    {t ? (
                      <div className="mt-1 text-sm">
                        <div className="font-semibold">{t.name || t.city || "—"}{t.code ? ` · ${t.code}` : ""}</div>
                        {t.street && <div className="text-muted-foreground">{t.street}</div>}
                        <div className="text-muted-foreground">{[t.city, t.state, t.zip].filter(Boolean).join(", ")}</div>
                        {t.phone && <div className="text-muted-foreground">P: {t.phone}</div>}
                        {t.distance_mi != null && <div className="text-muted-foreground">≈ {t.distance_mi} mi to stop</div>}
                      </div>
                    ) : (
                      <div className="mt-1 text-sm text-muted-foreground">Not available yet</div>
                    )}
                  </div>
                ))}
              </div>
              {(terminals.note || terminals.source) && (
                <div className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
                  {terminals.source === "lookup" ? "Live carrier service-center lookup" : terminals.source === "booking" ? "From carrier booking" : ""}
                  {terminals.note ? `${terminals.source ? " · " : ""}${terminals.note}` : ""}
                </div>
              )}
            </div>
          )}

          {hasDestination && (
            <button
              onClick={() => setShowAerial(true)}
              data-testid="detail-aerial-card"
              className="group relative block w-full overflow-hidden rounded-md border border-border bg-gradient-to-br from-zinc-900 to-zinc-800 text-left transition-shadow hover:shadow-lg"
            >
              <div className="flex items-center gap-3 px-4 py-4">
                <div className="rounded-full bg-white/10 p-3 transition-colors group-hover:bg-[hsl(var(--primary))]/30">
                  <Video className="h-5 w-5 text-[hsl(var(--primary))]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-sm font-bold text-white">View 3D Aerial</div>
                  <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-zinc-400">
                    <Building2 className="h-3 w-3 shrink-0" />
                    <span className="truncate">{q.dest_company || q.customer_name || "Destination"}{q.dest_city ? ` · ${q.dest_city}, ${q.dest_state || ""}` : ""}</span>
                  </div>
                </div>
                <span className="shrink-0 rounded-full border border-white/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white transition-colors group-hover:bg-white group-hover:text-zinc-900">View</span>
              </div>
            </button>
          )}

          {isBooked ? (
            <div className="border border-border bg-card" data-testid="booked-financials">
              <div className="border-b border-border px-4 py-3 font-heading text-base font-bold">Financial Summary</div>
              <div className="space-y-2 p-4 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Carrier</span><span className="font-semibold">{bookedCarrier}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Booked via</span><span className="font-semibold">{q.gtz_bol_number ? "GlobalTranz" : "R+L Carriers"}</span></div>
                <div className="my-2 border-t border-border" />
                <div className="flex justify-between">
                  <span className="font-semibold">True cost (at booking)</span>
                  <span className="metric-num text-base font-bold text-[hsl(var(--success))]" data-testid="booked-true-cost">{money(bookedCost)}</span>
                </div>
              </div>
            </div>
          ) : (
          <div className={`border bg-card ${marginBg(fin.margin_health)}`}>
            <div className="border-b border-border px-4 py-3 font-heading text-base font-bold">Financial Summary</div>
            <div className="space-y-2 p-4 text-sm">
              {[
                ["Raw carrier cost", money(q.carrier_cost)],
                ["Handling fee", money(fin.handling_fee)],
                ["Markup", `${fin.markup_pct ?? "—"}%`],
                ["Insurance", money(fin.insurance_fee || 0)],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="metric-num">{v}</span></div>
              ))}
              <div className="my-2 border-t border-border" />
              <div className="flex justify-between"><span className="font-semibold">Sell price</span><span className="metric-num font-bold">{money(q.sell_price)}</span></div>
              <div className="flex justify-between"><span className="font-semibold">Gross margin</span><span className={`metric-num font-bold ${marginColor(fin.margin_health)}`}>{money(q.margin_dollars)} · {pct(q.margin_pct)}</span></div>
              {fin.is_override && <div className="mt-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-2 py-1 text-xs text-[hsl(var(--warning))]">Price overridden{q.override_reason ? `: ${q.override_reason}` : ""}</div>}
            </div>
          </div>
          )}
        </div>
      </div>

      <CustomerSummary open={showCustomer} onOpenChange={setShowCustomer} form={custForm} result={custResult} financial={fin} />
      <DiagnosticsDrawer open={showDiag} onOpenChange={setShowDiag} result={custResult} selectedRate={selectedRate} />
      <ContextDiscussion
        refType="quote"
        refId={q.id}
        attach={{ type: "quote", id: q.id, label: q.quote_number, sub: q.customer_name || "" }}
        title="Quote Discussion"
      />

      <EmailQuoteDialog open={showEmail} onOpenChange={setShowEmail} quote={q} />
      <BolDialog open={showBol} onOpenChange={setShowBol} quote={q} onGenerated={handleBolGenerated} />
      <GtzBookDialog open={showGtzBook} onOpenChange={setShowGtzBook} quote={q} onBooked={load} />
      <FedExBookDialog open={showFedexBook} onOpenChange={setShowFedexBook} quote={q} onBooked={load} />
      <BookingEmailDialog quoteId={q.id} open={showBookingEmail} onOpenChange={(o) => { setShowBookingEmail(o); if (!o) load(); }} />
      <BolViewerDialog open={showBolViewer} onOpenChange={closeBolViewer} quote={q} initialBlobUrl={bolBlobUrl} />
      <ResyncBolDialog open={showResync} onOpenChange={setShowResync} quoteId={q.id} quoteNumber={q.quote_number} proNumber={q.bol_pro_number} onSynced={handleResynced} />
      <AerialViewDialog
        route={showAerial ? aerialRoute : null}
        onClose={() => setShowAerial(false)}
        onOpenQuote={() => setShowAerial(false)}
      />
    </div>
  );
}
